import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { FileCopy, Add, Delete } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { generateVendorToken, getAllVendorTokens, revokeVendorToken } from 'src/api/vendorTokens';
import { ROLES } from 'src/helpers/constants';

const RegistrationTokens = ({ type = 'vendor' }) => {
  const [tokens, setTokens] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadTokens();
  }, [type]);

  const loadTokens = async () => {
    try {
      const response = await getAllVendorTokens();
      // Filter tokens based on the type prop
      const filteredTokens = response.data.filter(token => token.role === (type === 'vendor' ? ROLES.VENDOR : ROLES.FUNDER));
      setTokens(filteredTokens);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load tokens',
        severity: 'error',
      });
    }
  };

  const handleOpenCreateDialog = () => {
    setNewEmail('');
    setNewFullName('');
    setNewCompanyName('');
    setOpenCreateDialog(true);
  };

  const handleSubmitCreate = async () => {
    try {
      const response = await generateVendorToken(
        type === 'vendor' ? ROLES.VENDOR : ROLES.FUNDER,
        newEmail,
        newFullName,
        newCompanyName
      );
      if (response.data && response.data.url) {
        const token = response.data.url.split('token=')[1];
        setNewToken(token);
        setOpenCreateDialog(false);
        setOpenDialog(true);
        loadTokens();
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Generate token error:', error);
      setSnackbar({ open: true, message: 'Failed to generate token', severity: 'error' });
    }
  };

  const handleDeleteToken = async (id) => {
    if (!window.confirm('Are you sure you want to delete this token?')) return;
    try {
      await revokeVendorToken(id);
      setSnackbar({ open: true, message: 'Token deleted', severity: 'success' });
      loadTokens();
    } catch (error) {
      console.error('Delete token error:', error);
      setSnackbar({ open: true, message: 'Failed to delete token', severity: 'error' });
    }
  };

  const handleCopyToken = (uuid) => {
    const signupUrl = `${window.location.origin}/account-type?token=${uuid}`;
    navigator.clipboard.writeText(signupUrl);
    setSnackbar({
      open: true,
      message: 'Signup URL copied to clipboard',
      severity: 'success',
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewToken('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box p={3}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4">{type === 'vendor' ? 'Vendor' : 'Funder'} Registration Tokens</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenCreateDialog}
            >
              Generate New Token
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Token</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell>{token.uuid}</TableCell>
                    <TableCell>{token.role}</TableCell>
                    <TableCell>{token.email}</TableCell>
                    <TableCell>{token.fullName}</TableCell>
                    <TableCell>{token.companyName || '-'}</TableCell>
                    <TableCell>{new Date(token.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{token.isUsed ? 'Used' : 'Not Used'}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToken(token.uuid)}
                      >
                        <FileCopy />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteToken(token.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>New Registration Token Generated</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={newToken}
            variant="outlined"
            margin="normal"
            label="Token"
            InputProps={{
              readOnly: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCopyToken(newToken)} color="primary">
            Copy Signup URL
          </Button>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
        <DialogTitle>Generate Registration Token</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            required
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Full Name"
            type="text"
            fullWidth
            required
            value={newFullName}
            onChange={e => setNewFullName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Company Name (optional)"
            type="text"
            fullWidth
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} color="primary">Cancel</Button>
          <Button onClick={handleSubmitCreate} color="primary">Generate</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegistrationTokens; 